import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { Crown, XCircle, User, Users, ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useToast } from "@/components/ui/use-toast";

// Types
interface RoomParticipant {
  id: string;
  participant_name: string;
  is_creator: boolean;
  created_at: string;
  room_id: string;
  can_edit?: boolean;
}

interface RoomKicked {
  id: string;
  participant_name: string;
  room_id: string;
  kicked_at: string;
}

interface RoomParticipantsProps {
  roomId: string;
  currentUser: string;
  showHeader?: boolean;
  isCreator: boolean;
  onParticipantsChange?: (count: number) => void;
}

// Debug component
const DebugInfo = ({ 
  status, 
  error, 
  lastEvent 
}: { 
  status: string; 
  error: string | null; 
  lastEvent: any 
}) => (
  <div style={{
    position: 'fixed',
    bottom: '10px',
    right: '10px',
    background: 'rgba(0,0,0,0.8)',
    color: '#fff',
    padding: '10px',
    borderRadius: '4px',
    fontSize: '12px',
    zIndex: 1000,
    maxWidth: '400px',
    maxHeight: '300px',
    overflow: 'auto',
    fontFamily: 'monospace'
  }}>
    <div><strong>Subscription Status:</strong> {status}</div>
    {error && <div style={{ color: '#ff6b6b' }}><strong>Error:</strong> {error}</div>}
    {lastEvent && (
      <div style={{ marginTop: '10px' }}>
        <div><strong>Last Event:</strong> {lastEvent.type} @ {new Date(lastEvent.timestamp).toLocaleTimeString()}</div>
        <pre style={{
          background: 'rgba(255,255,255,0.1)',
          padding: '5px',
          borderRadius: '4px',
          marginTop: '5px',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontSize: '10px',
          maxHeight: '150px',
          overflow: 'auto'
        }}>
          {JSON.stringify(lastEvent.payload, null, 2)}
        </pre>
      </div>
    )}
  </div>
);

export const RoomParticipants = ({ 
  roomId, 
  currentUser, 
  showHeader = true, 
  isCreator,
  onParticipantsChange 
}: RoomParticipantsProps) => {
  // All hooks must be called at the top level, before any conditional returns
  const [isOpen, setIsOpen] = useState(false);
  const [participants, setParticipants] = useState<RoomParticipant[]>([]);
  const [kickedParticipants, setKickedParticipants] = useState<RoomKicked[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string>('disconnected');
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const [lastEvent, setLastEvent] = useState<any>(null);
  const [banModal, setBanModal] = useState<{open: boolean, participant: string | null}>({ open: false, participant: null });
  const [justBanned, setJustBanned] = useState<string | null>(null);
  
  // All refs must be declared before any effects
  const { toast } = useToast();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const prevParticipantsCount = useRef(0);
  const retryCountRef = useRef(0);
  const isMounted = useRef(true);
  
  // Constants
  const MAX_RETRIES = 5;
  const RETRY_DELAY = 2000;
  
  // Notify parent when participants count changes
  useEffect(() => {
    if (onParticipantsChange && participants.length !== prevParticipantsCount.current) {
      onParticipantsChange(participants.length);
      prevParticipantsCount.current = participants.length;
    }
  }, [participants, onParticipantsChange]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Initial data fetch
  useEffect(() => {
    if (roomId) {
      fetchParticipants().catch(error => {
        console.error('Failed to load participants:', error);
        setError('Failed to load participants. Please try again.');
      });
    }
  }, [roomId]);

  // Fetch participants from the database
  const fetchParticipants = useCallback(async () => {
    if (!roomId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // First, get the room to find creator info
      const { data: roomData, error: roomError } = await supabase
        .from('rooms')
        .select('creator_name, creator_session_token')
        .eq('id', roomId)
        .single();
        
      if (roomError) throw roomError;
      if (!roomData) throw new Error('Room not found');
      
      const creatorName = roomData.creator_name;
      const creatorSessionToken = roomData.creator_session_token;
      
      // Get current user's session token
      const { data: { session } } = await supabase.auth.getSession();
      const currentSessionToken = session?.access_token || '';
      
      // Fetch participants with proper typing
      const { data: participantsData, error: participantsError, count: participantsCount } = await supabase
        .from('room_participants')
        .select('*', { count: 'exact' })
        .eq('room_id', roomId) as unknown as { 
          data: RoomParticipant[] | null, 
          error: any, 
          count: number | null 
        };
        
      if (participantsError) {
        console.error('[fetchParticipants] Error fetching participants:', participantsError);
        throw participantsError;
      }
      
      console.log(`[fetchParticipants] Found ${participantsCount} participants`);
      
      // Add is_creator flag to participants and ensure proper typing
      const participantsWithCreatorFlag: RoomParticipant[] = [];
      if (Array.isArray(participantsData)) {
        for (const participant of participantsData) {
          participantsWithCreatorFlag.push({
            id: participant.id,
            participant_name: participant.participant_name,
            is_creator: participant.participant_name === creatorName,
            created_at: participant.created_at,
            room_id: participant.room_id
          });
        }
      }
      
      // Fetch kicked users with proper typing
      console.log('[fetchParticipants] Fetching kicked users...');
      const { data: kickedData, error: kickedError, count: kickedCount } = await supabase
        .from('room_kicked')
        .select('*', { count: 'exact' })
        .eq('room_id', roomId) as unknown as {
          data: RoomKicked[] | null,
          error: any,
          count: number | null
        };
        
      if (kickedError) {
        console.error('[fetchParticipants] Error fetching kicked users:', kickedError);
        throw kickedError;
      }
      
      console.log(`[fetchParticipants] Found ${kickedCount} kicked users`);
      
      // Initialize active sessions for this room
      if (!activeSessions.current.has(roomId)) {
        activeSessions.current.set(roomId, new Map());
      }
      const roomSessions = activeSessions.current.get(roomId)!;
      
      // Update state with properly typed data and verify creator status
      const updatedParticipants = (participantsData || []).map(participant => {
        const participantName = participant.participant_name.toLowerCase();
        
        // Initialize session tracking for this participant
        if (!roomSessions.has(participantName)) {
          roomSessions.set(participantName, new Set());
        }
        
        // For existing participants, we don't have their session ID, so we'll use a placeholder
        // This ensures the username is marked as taken
        const sessions = roomSessions.get(participantName);
        if (sessions) {
          sessions.add(`existing-${participant.id}`);
        }
        
        // Check if this participant is the creator
        const isCreator = creatorName === participant.participant_name;
        
        return {
          ...participant,
          is_creator: isCreator
        };
      });
      
      setParticipants(updatedParticipants);
      setKickedParticipants(kickedData || []);
      
      // Update participant count in parent if needed
      const participantCount = updatedParticipants.length;
      console.log(`[fetchParticipants] Notifying parent of ${participantCount} participants`);
      if (onParticipantsChange) {
        onParticipantsChange(participantCount);
      }
      
      return participantsWithCreatorFlag;
    } catch (error) {
      console.error('[fetchParticipants] Error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load participants';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      throw error; // Re-throw to allow callers to handle the error
    } finally {
      setLoading(false);
      console.log('[fetchParticipants] Finished loading participants');
    }
  }, [roomId, onParticipantsChange, toast]);

  // Track active sessions for each username in each room
  // Structure: Map<roomId, Map<username, Set<sessionId>>>
  const activeSessions = useRef<Map<string, Map<string, Set<string>>>>(new Map());

  // Handle participant changes with immediate UI updates
  const handleParticipantChange = useCallback(async (payload: any) => {
    console.log('Participant change received:', payload);
    
    // Get the latest room data
    const { data: roomData } = await supabase
      .from('rooms')
      .select('creator_name')
      .eq('id', roomId)
      .single();
      
    // Get current user's session ID
    const { data: { session } } = await supabase.auth.getSession();
    const currentSessionId = session?.user?.id || 'anonymous';
    
    // Initialize session tracking for this room if it doesn't exist
    if (!activeSessions.current.has(roomId)) {
      activeSessions.current.set(roomId, new Map());
    }
    const roomSessions = activeSessions.current.get(roomId)!;
    
    // Update the participants list immediately for better UX
    if (payload.eventType === 'INSERT') {
      const newParticipant = payload.new;
      const participantName = newParticipant.participant_name;
      const participantNameLower = participantName.toLowerCase();
      const participantId = newParticipant.id;
      
      // Check if this username is already in use in this room
      const isUsernameTaken = Array.from(roomSessions.entries()).some(
        ([username, sessions]) => 
          username.toLowerCase() === participantNameLower && 
          username !== currentUser?.toLowerCase()
      );
      
      if (isUsernameTaken) {
        console.warn('Username already in use:', participantName);
        toast({
          title: 'Username taken',
          description: `The username "${participantName}" is already in use in this room`,
          variant: 'destructive',
          duration: 5000
        });
        return; // Don't add the participant
      }
      
      // Add the session for this username
      if (!roomSessions.has(participantNameLower)) {
        roomSessions.set(participantNameLower, new Set());
      }
      roomSessions.get(participantNameLower)?.add(currentSessionId);
      
      // Show notification for new participant (except for the current user)
      if (currentUser && participantNameLower !== currentUser.toLowerCase()) {
        toast({
          title: 'New participant joined',
          description: `${participantName} has joined the room`,
          duration: 3000
        });
      }
      
      // Add new participant with optimistic update
      setParticipants(prev => {
        // Skip if this is the current user's own join event
        if (currentUser && participantNameLower === currentUser.toLowerCase()) {
          return prev;
        }
        
        // Check if participant already exists (case-insensitive check)
        const existingParticipant = prev.find(p => 
          p.participant_name.toLowerCase() === participantNameLower
        );
        
        if (existingParticipant) {
          console.log('Participant already exists:', existingParticipant);
          return prev; // Shouldn't happen due to previous check, but just in case
        }
        
        // Verify if the new participant is the creator
        const isCreator = roomData?.creator_name === newParticipant.participant_name;
        
        console.log('Adding new participant:', newParticipant);
        return [...prev, {
          ...newParticipant,
          is_creator: isCreator,
          created_at: newParticipant.created_at || new Date().toISOString()
        }];
      });
      
    } else if (payload.eventType === 'DELETE') {
      // Remove participant
      const oldParticipant = payload.old;
      if (!oldParticipant) {
        console.error('No old participant data in DELETE event');
        return;
      }
      
      const participantName = oldParticipant.participant_name;
      const participantNameLower = participantName?.toLowerCase();
      console.log('Removing participant:', participantName, 'from room:', roomId);
      
      if (!participantName) {
        console.error('No participant name in DELETE event');
        return;
      }
      
      // Remove the session for this username
      if (participantNameLower && roomSessions.has(participantNameLower)) {
        const sessions = roomSessions.get(participantNameLower)!;
        sessions.delete(currentSessionId);
        if (sessions.size === 0) {
          roomSessions.delete(participantNameLower);
        }
      }
      
      // Remove the participant from the list
      setParticipants(prev => {
        const updated = prev.filter(p => 
          p.id !== oldParticipant.id && 
          p.participant_name.toLowerCase() !== participantNameLower
        );
        console.log(`Updated participants after removal (${prev.length} -> ${updated.length}):`, updated);
        return updated;
      });
      
      // Show notification for participant leaving (except for the current user)
      if (currentUser && participantNameLower !== currentUser.toLowerCase()) {
        toast({
          title: 'Participant left',
          description: `${participantName} has left the room`,
          duration: 3000
        });
      }
    }
  }, [toast, currentUser]);
  
  // Handle ban changes with immediate UI updates
  const handleBanChange = useCallback((payload: any) => {
    console.log('Ban change received:', payload);
    
    if (payload.eventType === 'INSERT') {
      const { room_id, participant_name, kicked_by } = payload.new;
      
      // Immediately update the UI
      setParticipants(prev => prev.filter(p => p.participant_name !== participant_name));
      setKickedParticipants(prev => [
        ...prev.filter(k => k.participant_name !== participant_name),
        {
          id: `temp-${Date.now()}`,
          room_id,
          participant_name,
          kicked_by: kicked_by || 'system',
          kicked_at: new Date().toISOString()
        }
      ]);
      
      // If the current user was banned, show a message and redirect
      if (currentUser && currentUser.toLowerCase() === participant_name.toLowerCase()) {
        toast({
          title: 'You have been banned',
          description: 'You are no longer a participant in this room.',
          variant: 'destructive',
          duration: 5000
        });
        
        // Redirect after a short delay
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
      }
    }
  }, [currentUser, toast]);

  // Handle banning a participant
  const handleBan = useCallback(async (participantName: string) => {
    if (!isCreator || !roomId || !currentUser) {
      console.error('Missing required parameters for ban:', { isCreator, roomId, currentUser });
      toast({
        title: 'Error',
        description: 'You do not have permission to ban participants.',
        variant: 'destructive',
      });
      return;
    }
    
    // Don't allow banning self
    if (participantName.toLowerCase() === currentUser.toLowerCase()) {
      toast({
        title: 'Cannot Ban Yourself',
        description: 'You cannot ban yourself from the room. Please use the Leave Room option instead.',
        variant: 'destructive',
      });
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('Banning participant:', { roomId, participantName, currentUser });
      
      // First, verify the current user is the room creator
      const { data: roomData, error: roomError } = await supabase
        .from('rooms')
        .select('creator_name')
        .eq('id', roomId)
        .single();
        
      if (roomError) throw roomError;
      if (!roomData) throw new Error('Room not found');
      
      if (roomData.creator_name !== currentUser) {
        throw new Error('Only the room creator can ban participants');
      }
      
      // Immediately update the UI to show the ban is in progress
      setParticipants(prev => prev.filter(p => p.participant_name !== participantName));
      setKickedParticipants(prev => [...prev, {
        id: `temp-${Date.now()}`,
        room_id: roomId,
        participant_name: participantName,
        kicked_at: new Date().toISOString()
      }]);

      // Check if participant exists in the room
      const { data: participantData, error: participantError } = await supabase
        .from('room_participants')
        .select('*')
        .eq('room_id', roomId)
        .eq('participant_name', participantName)
        .maybeSingle();

      if (participantError) throw participantError;
      
      if (!participantData) {
        // Check if already banned
        const { data: alreadyBanned } = await supabase
          .from('room_kicked')
          .select('*')
          .eq('room_id', roomId)
          .eq('participant_name', participantName)
          .maybeSingle();
          
        if (alreadyBanned) {
          throw new Error('This user is already banned');
        } else {
          throw new Error('Participant not found in this room');
        }
      }
      
      // Call the ban_participant function with the correct parameter names
      const { data: banResult, error: banError } = await supabase.rpc('ban_participant', {
        room_id_param: roomId,
        participant_name_param: participantName,
        admin_name: currentUser
      });
      
      if (banError) throw banError;
      
      if (!banResult) {
        throw new Error('Failed to ban participant');
      }
      
      setJustBanned(participantName);
      setTimeout(() => setJustBanned(null), 3000);
      
      // Force refresh the participants list
      await fetchParticipants();
      
      toast({
        title: 'Participant banned',
        description: `${participantName} has been banned from the room`,
      });
      
    } catch (err) {
      console.error('Error banning participant:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to ban participant';
      setError(errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setBanModal({ open: false, participant: null });
    }
  }, [roomId, isCreator, currentUser, fetchParticipants, toast]);

  // Set up real-time subscriptions for participants and bans with retry logic
  useEffect(() => {
    if (!roomId) {
      console.log('No roomId, skipping subscription setup');
      return;
    }
    
    console.log(`[${new Date().toISOString()}] Setting up subscriptions for room: ${roomId}`);
    
    let retryCount = 0;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let retryTimeout: NodeJS.Timeout;
    let isSubscribed = false;
    
    const setupSubscription = async (currentRetryCount = 0) => {
      if (!isMounted.current || isSubscribed) return;
      
      try {
        console.log(`[${new Date().toISOString()}] Setting up subscriptions (attempt ${currentRetryCount + 1}/${MAX_RETRIES})...`);
        setSubscriptionStatus('connecting');
        setSubscriptionError(null);
        
        // First, fetch the current participants
        await fetchParticipants();
        
        // Clean up any existing channel
        if (channelRef.current) {
          console.log('Cleaning up existing channel...');
          try {
            await supabase.removeChannel(channelRef.current);
          } catch (err) {
            console.error('Error during channel cleanup:', err);
          } finally {
            channelRef.current = null;
          }
        }
        
        // Create a new channel with a unique name
        const channelName = `room_${roomId}_${Date.now()}`;
        console.log(`Creating new channel: ${channelName}`);
        
        channel = supabase.channel(channelName, {
          config: {
            broadcast: { ack: true, self: true },
            presence: { key: roomId },
          }
        });
        
        // Store the channel reference before setting up listeners
        channelRef.current = channel;
        isSubscribed = true;
        
        // Log channel status changes
        const handleSystemEvent = (event: any) => {
          console.log('System event:', event);
          
          if (!isMounted.current) return;
          
          switch (event.event) {
            case 'CHANNEL_ERROR': {
              const errorMessage = event.error || 'Unknown channel error';
              console.error('Channel error:', errorMessage);
              setSubscriptionError(`Connection error: ${errorMessage}`);
              
              // Only retry if we haven't exceeded max retries
              if (retryCount < MAX_RETRIES) {
                const delay = Math.min(RETRY_DELAY * Math.pow(2, retryCount), 30000);
                console.log(`Will retry in ${delay}ms...`);
                
                retryTimeout = setTimeout(() => {
                  if (isMounted.current) {
                    isSubscribed = false;
                    setupSubscription(retryCount + 1);
                  }
                }, delay);
                
                retryCount++;
              } else {
                setSubscriptionError('Failed to connect. Please refresh the page.');
              }
              break;
            }
            
            case 'CHANNEL_JOINED':
              console.log('Successfully joined channel');
              setSubscriptionStatus('SUBSCRIBED');
              setSubscriptionError(null);
              retryCount = 0; // Reset retry count on successful join
              break;
              
            case 'SUBSCRIPTION_ERROR':
              console.error('Subscription error:', event);
              setSubscriptionError('Connection error. Attempting to reconnect...');
              break;
              
            default:
              console.log('Unhandled system event:', event);
          }
        };
        
        // Set up event listeners
        channel
          .on('broadcast', { event: '*' }, (payload) => {
            console.log('Broadcast received:', payload);
          })
          .on('system', '*', handleSystemEvent);

        // Subscribe to room_participants changes with immediate UI updates
        const changes = channel
          .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'room_participants',
            filter: `room_id=eq.${roomId}`
          }, async (payload: any) => {
            console.log('Room participants change received:', payload);
            if (!isMounted.current) return;
            
            setLastEvent({
              type: 'participants_change',
              payload,
              timestamp: new Date().toISOString()
            });
            
            // Process the change immediately
            handleParticipantChange(payload);
            
            // Refresh participants list to ensure consistency
            try {
              await fetchParticipants();
            } catch (error) {
              console.error('Error refreshing participants:', error);
            }
            
            // Broadcast the change to other clients
            if (payload.eventType === 'INSERT') {
              channel?.send({
                type: 'broadcast',
                event: 'participant_joined',
                payload: {
                  participant: payload.new,
                  timestamp: new Date().toISOString()
                }
              }).catch(console.error);
            }
          })
          .on('system', 'postgres_changes_error', (payload) => {
            console.error('Postgres changes error:', payload);
            const errorMsg = payload.message || 'Unknown PostgreSQL error';
            setSubscriptionError(`Subscription error: ${errorMsg}`);
            
            // Log additional debug info
            console.error('PostgreSQL subscription error details:', {
              payload,
              roomId,
              currentUser,
              retryCount: retryCountRef.current
            });
            
            // Attempt to resubscribe on error with exponential backoff
            if (isMounted.current && retryCountRef.current < MAX_RETRIES) {
              const delay = Math.min(RETRY_DELAY * Math.pow(2, retryCountRef.current), 30000);
              console.log(`Will retry PostgreSQL subscription in ${delay}ms...`);
              
              retryTimeout = setTimeout(() => {
                if (isMounted.current) {
                  retryCountRef.current++;
                  setupSubscription(retryCountRef.current).catch(console.error);
                }
              }, delay);
            } else {
              console.error('Max retries reached for PostgreSQL subscription');
              setSubscriptionError('Failed to establish real-time connection. Please refresh the page.');
            }
          })
          .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'room_kicked',
            filter: `room_id=eq.${roomId}`
          }, (payload: any) => {
            console.log('Kicked list change received:', payload);
            if (!isMounted.current) return;
            
            setLastEvent({
              type: 'kicked_change',
              payload,
              timestamp: new Date().toISOString()
            });
            
            // Immediate UI update for bans
            handleBanChange({
              ...payload,
              new: {
                ...payload.new,
                room_id: roomId
              }
            });
          })
          // Listen for broadcast events
          .on('broadcast', { event: 'participant_joined' }, (payload) => {
            console.log('Received participant_joined broadcast:', payload);
            if (!isMounted.current) return;
            
            // Update UI for the new participant
            if (payload.payload?.participant) {
              handleParticipantChange({
                eventType: 'INSERT',
                new: payload.payload.participant
              });
              
              // Refresh participants list to ensure consistency
              fetchParticipants().catch(console.error);
            }
          })
          .subscribe((status: string) => {
            console.log(`[${new Date().toISOString()}] Subscription status:`, status);
            setSubscriptionStatus(status);
            
            if (status === 'CHANNEL_ERROR' || status === 'CLOSED') {
              const errorMsg = `Subscription error: ${status}`;
              console.error(errorMsg);
              setSubscriptionError(errorMsg);
              
              // Auto-retry with exponential backoff
              if (isMounted.current && retryCount < MAX_RETRIES) {
                const delay = Math.min(RETRY_DELAY * Math.pow(2, retryCount), 30000); // Max 30s delay
                console.log(`Retrying subscription in ${delay}ms...`);
                setTimeout(() => setupSubscription(retryCount + 1), delay);
              }
            } else if (status === 'SUBSCRIBED') {
              console.log('Successfully subscribed to room updates');
              setSubscriptionError(null);
              retryCountRef.current = 0; // Reset retry counter on success
            }
          });

        channelRef.current = channel;
        
        // Track presence when joining
        channel.track({
          user: currentUser,
          online_at: new Date().toISOString()
        }).catch(console.error);
        
      } catch (error) {
        console.error('Error in setupSubscription:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to set up subscriptions';
        setSubscriptionStatus('error');
        setSubscriptionError(errorMessage);
        
        // Retry with exponential backoff
        if (isMounted.current && retryCount < MAX_RETRIES) {
          const delay = Math.min(RETRY_DELAY * Math.pow(2, retryCount), 30000); // Max 30s delay
          console.log(`Retrying after error in ${delay}ms...`);
          retryTimeout = setTimeout(() => setupSubscription(retryCount + 1), delay);
        }
      }
    };
    
    setupSubscription(0);
    
    // Setup presence to detect disconnections
    const presenceInterval = setInterval(() => {
      if (isMounted.current && channelRef.current && subscriptionStatus === 'SUBSCRIBED') {
        channelRef.current.track({
          user: currentUser,
          online_at: new Date().toISOString()
        }).catch(console.error);
      }
    }, 30000); // Every 30 seconds
    
    // Cleanup function
    return () => {
      if (channelRef.current) console.log('Cleaning up subscriptions...');
      isMounted.current = false;
      isSubscribed = false;
      clearInterval(presenceInterval);
      clearTimeout(retryTimeout);
      
      const cleanupChannel = async () => {
        if (!channelRef.current) return;
        
        console.log('Starting channel cleanup...');
        const channelToRemove = channelRef.current;
        channelRef.current = null;
        
        try {
          // Add a small delay to ensure any in-flight operations complete
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Check if channel is still active
          if (channelToRemove.state === 'joined') {
            console.log('Removing channel...');
            try {
              // Use the channel's unsubscribe method first
              channelToRemove.unsubscribe();
              
              // Then remove the channel from supabase
              const result = await supabase.removeChannel(channelToRemove);
              
              if (result === 'ok') {
                console.log('Successfully removed channel');
              } else {
                console.log('Channel removal status:', result);
              }
            } catch (error) {
              console.error('Error during channel removal:', error);
            }
          } else {
            console.log('Channel already closed');
          }
        } catch (err) {
          console.error('Error during channel cleanup:', err);
        }
      
      };
      
      // Run cleanup synchronously to prevent race conditions
      cleanupChannel().catch(console.error);
    };
  }, [roomId, fetchParticipants]); // Removed handleParticipantChange and handleKickChange from deps as they're not used directly

  // Render loading state
  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-8 bg-gray-200 rounded w-3/4"></div>
          ))}
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="p-4 text-red-600">
        <p>Error: {error}</p>
        <button 
          onClick={fetchParticipants}
          className="mt-2 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  // Get the current user's display name and normalize for comparison
  const currentUserName = currentUser || '';
  
  // Helper function to safely compare usernames (case-insensitive)
  const usernamesMatch = (name1: string | undefined | null, name2: string | undefined | null): boolean => {
    if (!name1 || !name2) return false;
    return name1.toLowerCase() === name2.toLowerCase();
  };
  
  // Debug info
  console.log('Current user:', currentUserName);
  console.log('Is creator:', isCreator);
  console.log('All participants:', participants);
  
  // Filter out current user from participants list and sort with admin first
  const otherParticipants = participants
    .filter(p => !usernamesMatch(p.participant_name, currentUserName))
    .sort((a, b) => {
      // Sort admins first, then by name
      if (a.is_creator && !b.is_creator) return -1;
      if (!a.is_creator && b.is_creator) return 1;
      return (a.participant_name || '').localeCompare(b.participant_name || '');
    });
  
  // Get current user data if exists in participants
  const currentUserData = participants.find(p => 
    usernamesMatch(p.participant_name, currentUserName)
  ) || {
    id: `user-${Date.now()}`,
    participant_name: currentUserName,
    is_creator: isCreator,
    created_at: new Date().toISOString(),
    room_id: roomId
  };
  
  // Debug logging in development
  if (process.env.NODE_ENV === 'development') {
    console.log('Current user:', currentUserData?.participant_name);
    console.log('Is creator:', isCreator);
    console.log('All participants:', participants);
  }

  // Determine connection status text and color
  const connectionStatus = {
    text: subscriptionStatus === 'SUBSCRIBED' ? 'Connected' : 
          subscriptionStatus === 'CHANNEL_ERROR' ? 'Disconnected' : 'Connecting...',
    color: subscriptionStatus === 'SUBSCRIBED' ? 'bg-green-500' : 'bg-yellow-500'
  };

  return (
    <div className="space-y-2">
      {/* Debug overlay in development - Hidden by default */}
      {false && process.env.NODE_ENV === 'development' && (
        <DebugInfo 
          status={subscriptionStatus} 
          error={subscriptionError}
          lastEvent={lastEvent}
        />
      )}
      
      {/* Dropdown Toggle */}
      <div className="border rounded-lg overflow-hidden">
        <button 
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-left text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-2">
            <Users className="w-4 h-4 text-gray-500" />
            <span>Participants ({participants.length})</span>
            <div className="flex items-center space-x-1 text-xs text-gray-500 font-normal">
              <span className={`w-2 h-2 rounded-full ${connectionStatus.color}`}></span>
              <span>{connectionStatus.text}</span>
            </div>
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
        </button>
        
        {/* Dropdown Content */}
        {isOpen && (
          <div className="border-t">
            <div className="p-2 space-y-2">
              {/* Current User Section */}
              {currentUserData && (
                <div className="mb-3">
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-between py-2 px-3 rounded-md bg-blue-50"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <User className="w-5 h-5 text-blue-500" />
                        <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white bg-green-500"></div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-900">{currentUserData.participant_name}</span>
                        <span className="text-xs text-blue-600">You</span>
                        {currentUserData.is_creator && (
                          <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700">
                            <Crown className="w-3 h-3 mr-1" />
                            Admin
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                </div>
              )}
              
              {/* Other Participants Section */}
              {otherParticipants.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider px-2 mb-1">
                    Other Members ({otherParticipants.length})
                  </h4>
                  <div className="space-y-1">
                    <AnimatePresence>
                      {otherParticipants.map((participant) => (
                        <motion.div
                          key={participant.id}
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -5 }}
                          className="flex items-center justify-between py-1.5 px-3 rounded-md hover:bg-gray-50 group"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="relative">
                              <User className="w-5 h-5 text-gray-400" />
                              <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white bg-green-500"></div>
                            </div>
                            <div>
                              <div className="flex items-center space-x-2">
                                <span className="text-gray-900">{participant.participant_name}</span>
                                {participant.is_creator && (
                                  <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700">
                                    <Crown className="w-3 h-3 mr-1" />
                                    Admin
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500">
                                {justBanned === participant.participant_name ? 'Banned' : 'Online'}
                              </div>
                            </div>
                          </div>
                          
                          {(() => {
                            console.log(`Rendering ban button for ${participant.participant_name}:`, {
                              isCreator,
                              isParticipantCreator: participant.is_creator,
                              shouldShowBan: isCreator && !participant.is_creator && !usernamesMatch(participant.participant_name, currentUserName)
                            });
                            
                            if (isCreator && !participant.is_creator && !usernamesMatch(participant.participant_name, currentUserName)) {
                              return (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setBanModal({ open: true, participant: participant.participant_name });
                                  }}
                                  className="p-1 text-gray-300 hover:text-red-500 rounded-full transition-colors"
                                  title="Ban participant"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              );
                            }
                            return null;
                          })()}
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Ban Confirmation Modal */}
      {banModal.open && banModal.participant && !usernamesMatch(banModal.participant, currentUserName) && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-sm w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold mb-3">Ban Participant</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to ban <span className="font-medium">{banModal.participant}</span>? 
              They won't be able to rejoin this room.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setBanModal({ open: false, participant: null })}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleBan(banModal.participant!)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Banning...' : 'Ban'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
